/**
 * useVoiceNote — record a voice note in the browser and upload it to the
 * server-side transcription pipeline (POST /voice/note).
 *
 * Ported from the legacy voice-wake.js recording flow: getUserMedia ->
 * MediaRecorder (audio/webm) -> on stop, upload the blob. Auto-stops after
 * 60s. This is an additive capture option; existing React voice input
 * (useVoiceInput, browser STT) is unchanged.
 */
import { useCallback, useRef, useState } from 'react';
import { voiceNote, type VoiceNoteResponse } from '../api/voice';

const MAX_RECORD_MS = 60_000;

export interface UseVoiceNoteOptions {
  onResult?: (result: VoiceNoteResponse) => void;
  onError?: (message: string) => void;
}

export interface UseVoiceNoteReturn {
  isSupported: boolean;
  isRecording: boolean;
  isUploading: boolean;
  error: string | null;
  lastResult: VoiceNoteResponse | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}

function detectSupport(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function useVoiceNote(options: UseVoiceNoteOptions = {}): UseVoiceNoteReturn {
  const { onResult, onError } = options;
  const [isSupported] = useState(detectSupport);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<VoiceNoteResponse | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const autoStopRef = useRef<number | null>(null);

  const teardownStream = useCallback(() => {
    if (autoStopRef.current !== null) {
      window.clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported || isRecording) return;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        teardownStream();
        setIsRecording(false);
        if (blob.size === 0) return;
        setIsUploading(true);
        try {
          const result = await voiceNote(blob);
          setLastResult(result);
          onResult?.(result);
        } catch (e) {
          const message = e instanceof Error ? e.message : 'Voice note failed.';
          setError(message);
          onError?.(message);
        } finally {
          setIsUploading(false);
        }
      };

      recorder.start();
      setIsRecording(true);
      autoStopRef.current = window.setTimeout(() => {
        recorderRef.current?.stop();
      }, MAX_RECORD_MS);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Microphone access was blocked.';
      setError(message);
      onError?.(message);
      teardownStream();
      setIsRecording(false);
    }
  }, [isSupported, isRecording, teardownStream, onResult, onError]);

  return { isSupported, isRecording, isUploading, error, lastResult, startRecording, stopRecording };
}
